import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProgramReadiness } from '../../model/programs.types';
import { isReady, metCount, totalCount } from '../../model/programs.readiness';

import * as styles from './ReadinessIndicator.css';

type ReadinessIndicatorProps = {
  readiness: ProgramReadiness;
  /** Size in pixels. Default 16. */
  size?: number;
};

/**
 * Visualises readiness state as either a green progress ring (when some
 * criteria are unmet) or a solid green circle with a white check (when all
 * criteria are met). Used by both `StatusTag` (badge on Programs list /
 * Offers dropdown) and `ProgramReadinessPopover` header pill so the same
 * visual carries through from trigger to expanded detail.
 *
 * The ring is rendered as an SVG so the arc length can express progress
 * deterministically — `metCount / totalCount` of the circumference.
 */
export const ReadinessIndicator = ({
  readiness,
  size = 16,
}: ReadinessIndicatorProps) => {
  if (isReady(readiness)) {
    return (
      <span
        className={styles.solid}
        style={{ width: size, height: size }}
        aria-hidden
      >
        <FontAwesomeIcon icon={faCheck} className={styles.check} />
      </span>
    );
  }

  // Progress ring: render two stacked circles. The background ring is light
  // gray; the progress arc is green, drawn from 12 o'clock (rotated -90deg)
  // and length-controlled via stroke-dasharray.
  const stroke = Math.max(2, Math.round(size / 8));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = metCount(readiness) / totalCount();
  const dashOffset = circumference * (1 - progress);

  return (
    <svg
      className={styles.ring}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        className={styles.ringTrack}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        className={styles.ringProgress}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};
